/* ============================================================================
   ABRIR EL ARCHIVO DE MAPA Y SERVIRLO AL MAPA

   El archivo `.pmtiles` se lee POR PEDAZOS: se abre una vez y después se leen
   solo los mosaicos que el mapa pide. Un archivo de 2 GB se usa igual que uno
   de 20 MB, y nada se carga en memoria de más.

   ══════════════════════════════════════════════════════════════════════════
   EL ARCHIVO NO SE COPIA A NINGÚN LADO

   Se guarda una referencia al archivo que el piloto eligió, y se lee de ahí.
   Copiarlo al almacenamiento del navegador duplicaría dos gigabytes y llenaría
   el dispositivo.

   La contra: **el navegador pierde el permiso al cerrar la aplicación** y hay
   que volver a elegirlo. Es una limitación real y hay que decirla, no
   esconderla: un piloto que cree tener el mapa cargado y en vuelo descubre que
   no, está peor que uno que sabe que tiene que elegirlo antes de salir.
   ══════════════════════════════════════════════════════════════════════════
   ========================================================================== */

import { PMTiles, FileSource } from "pmtiles";

/** El archivo abierto en esta sesión. */
let abierto = null;

/**
 * Abre un archivo de mapa.
 *
 * @returns {Promise<{ok: boolean, cabecera: object|null, motivo: string|null}>}
 */
export async function abrirArchivo(archivo) {
  try {
    const pm = new PMTiles(new FileSource(archivo));
    const cabecera = await pm.getHeader();

    /* Un archivo que abre pero no declara cobertura no sirve: no se puede
       saber qué zona cubre ni avisar cuando la ruta se sale. */
    if (!Number.isFinite(cabecera?.maxZoom)) {
      return { ok: false, cabecera: null, motivo: "El archivo no declara qué zona cubre." };
    }

    abierto = { pm, cabecera, nombre: archivo?.name || "mapa" };
    return { ok: true, cabecera, motivo: null };
  } catch (e) {
    return {
      ok: false,
      cabecera: null,
      /* El motivo real, no «error al abrir»: casi siempre es un archivo
         cortado o de otro formato, y saberlo ahorra media hora. */
      motivo: `No se pudo abrir el archivo: ${e?.message || "formato no reconocido"}. `
        + "Verifica que la descarga haya terminado y que sea un .pmtiles.",
    };
  }
}

/** ¿Hay un mapa abierto? */
export function hayArchivo() {
  return abierto !== null;
}

/** El archivo abierto, o null. */
export function archivoActual() {
  return abierto ? { nombre: abierto.nombre, cabecera: abierto.cabecera } : null;
}

/** Cierra el archivo. */
export function cerrarArchivo() {
  abierto = null;
}

/**
 * Capa de Leaflet que lee del archivo.
 *
 * Se crea una clase propia en vez de usar una dirección: los mosaicos no vienen
 * de una URL sino de leer pedazos del archivo, y hay que entregarle a Leaflet
 * los bytes ya resueltos.
 */
export function crearCapaDeArchivo(L, opciones = {}) {
  if (!abierto) return null;

  const CapaArchivo = L.GridLayer.extend({
    createTile(coords, hecho) {
      const img = document.createElement("img");

      /* Fuera de lo que el archivo cubre no hay mosaico, y eso NO es un error:
         es el borde del mapa. Se devuelve un mosaico vacío en vez de dejar que
         Leaflet marque error y reintente para siempre. */
      abierto.pm.getZxy(coords.z, coords.x, coords.y)
        .then((res) => {
          if (!res?.data) { hecho(null, img); return; }
          const blob = new Blob([res.data]);
          img.src = URL.createObjectURL(blob);
          img.onload = () => { URL.revokeObjectURL(img.src); hecho(null, img); };
          img.onerror = () => { URL.revokeObjectURL(img.src); hecho(null, img); };
        })
        .catch(() => { hecho(null, img); });

      return img;
    },
  });

  return new CapaArchivo({
    minZoom: abierto.cabecera.minZoom ?? 0,
    maxZoom: 19,
    /* El archivo puede no llegar al zoom que el piloto quiere: en vez de dejar
       el mapa en blanco, Leaflet agranda el último nivel que sí existe. Se ve
       borroso, pero se ve — y en vuelo eso es mejor que nada. */
    maxNativeZoom: abierto.cabecera.maxZoom ?? 14,
    attribution: opciones.atribucion
      || "Mapa local · <a href='https://www.openstreetmap.org/copyright'>© OpenStreetMap</a>",
    ...opciones,
  });
}
